import type { ComponentProps, Dispatch, SetStateAction } from "react";
import type * as type from "@vTypes";
import TableHeader from "@components/feature/rosterTable/TableHeader.tsx";
import { RosterShiftLegendChips } from "@components/feature/rosterTable/RosterShiftLegendChips";
import { cn } from "@/lib/utils";
import type { NurseResponse } from "@/types/nurse-type";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import type { RosterTableLayoutConfig } from "@/components/nurse/rosterColumnHoverLayout";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type LegendProps = ComponentProps<typeof RosterShiftLegendChips>;

type RosterCreateTableHeaderProps<TNurse extends NurseResponse> = {
  readonly defaultShiftCode: type.availableShifts[];
  readonly copyNurses: TNurse[];
  readonly setCopyNurses: Dispatch<SetStateAction<TNurse[]>>;
  readonly onSortedRows?: (rows: TNurse[]) => void;
  readonly currentYear: number;
  readonly currentMonth: number;
  readonly holidayDates: string[];
  readonly prevTailData?: PrevTailData | null;
  readonly showPerShiftStats: boolean;
  readonly setShowPerShiftStats: Dispatch<SetStateAction<boolean>>;
  readonly rosterCreateShiftLegend: LegendProps["displayShifts"];
  readonly selectionFocusOnClick?: boolean;
  readonly layoutConfig?: RosterTableLayoutConfig;
};

export function RosterCreateTableHeader<TNurse extends NurseResponse>({
  defaultShiftCode,
  copyNurses,
  setCopyNurses,
  onSortedRows,
  currentYear,
  currentMonth,
  holidayDates,
  prevTailData,
  showPerShiftStats,
  setShowPerShiftStats,
  rosterCreateShiftLegend,
  selectionFocusOnClick,
  layoutConfig,
}: RosterCreateTableHeaderProps<TNurse>) {
  const toggleLabel = showPerShiftStats
    ? "개별 근무코드 통계 숨기기"
    : "개별 근무코드 통계 보기";

  return (
    <div className="flex gap-[10px]">
      <TableHeader
        defaultShiftCode={defaultShiftCode}
        copyNurses={copyNurses}
        setCopyNurses={setCopyNurses}
        onSortedRows={onSortedRows}
        currentYear={currentYear}
        currentMonth={currentMonth}
        holidayDates={holidayDates}
        prevTailData={prevTailData}
        selectionFocusOnClick={selectionFocusOnClick}
        layoutConfig={layoutConfig}
      />
      <div className="size-10 relative flex shrink-0 items-end">
        <div className="flex absolute -bottom-[17px] -left-[1px] bg-white rounded-full">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={cn(
                  "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                  "transition-opacity hover:opacity-90",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                )}
                aria-pressed={showPerShiftStats}
                aria-label={toggleLabel}
                onClick={() => setShowPerShiftStats((prev) => !prev)}
              >
                <img
                  src="/asset/icon/config/icon_1.svg"
                  alt=""
                  aria-hidden
                  className={cn(
                    "size-7 object-contain transition-[filter,transform] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]",
                    showPerShiftStats ? "rotate-90" : "-rotate-90"
                  )}
                  draggable={false}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{toggleLabel}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {showPerShiftStats ? (
        <div className="flex min-h-10 items-start pt-1 pr-2">
          <RosterShiftLegendChips
            displayShifts={rosterCreateShiftLegend}
            defaultShiftCode={defaultShiftCode}
            className="animate-in fade-in duration-200"
          />
        </div>
      ) : null}
    </div>
  );
}
